import React from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAskX1PageContext } from '../components/askx1/AskX1ContextProvider'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  XCircle,
  ArrowRightLeft,
  Database,
  Zap,
  BarChart3,
  Code,
  FileText,
  Layers,
  ChevronDown,
  ChevronRight,
  Eye,
  Coins
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import explorerService from '@/services/explorerService'
import { configService } from '@/config'
import { log } from '@/utils/logger'
import { CopyButton } from '@/components/common/CopyButton'
import VirtualTable from '@/components/common/VirtualTable'
import { AddressWithName } from '@/components/common/AddressWithName'
import { useAddressNames } from '@/hooks/useAddressNames'
import { AddressDisplay } from '@/components/common/AddressDisplay'
import { TokenCell } from '@/components/portfolio/suite/TokenCell'
import { useProgramLabels } from '@/hooks/useProgramLabels'
import { BadgeCheck } from 'lucide-react'
import {
  buildTxSummary,
  formatCompactAmount,
  resolveInstructionProgramId,
  resolveMintSymbol,
  type TxSummaryAsset,
} from '@/utils/txSummary'
import { tokenMetadataService } from '@/services/tokenMetadataService'
import { getX1TokenIdentity } from '@/utils/knownX1Tokens'
import { recognizeConfidentialInstruction, isTokenReallocate, confidentialTransferParticipants } from '@/utils/confidentialTransfer'
import { ConfidentialTransferPanel } from '@/components/transaction/ConfidentialTransferPanel'

// Known instruction discriminators for parsing
const INSTRUCTION_DISCRIMINATORS: Record<string, Record<string, string>> = {
  // Compute Budget program (1-byte discriminator)
  'ComputeBudget111111111111111111111111111111': {
    '00': 'RequestUnitsDeprecated',
    '01': 'RequestHeapFrame',
    '02': 'SetComputeUnitLimit',
    '03': 'SetComputeUnitPrice',
    '04': 'SetLoadedAccountsDataSizeLimit',
  },
  // X1 Oracle (Anchor 8-byte discriminator - first 2 bytes shown)
  'CcgTMiYkgVfz7cAGkD6835BqfycG5N5Y4aPPHYW1EvKx': {
    '9a4857': 'BatchSetPrices',
    'default': 'SetPrice',
  },
  // X1 Stake Pool
  'XPoo1Fx6KNgeAzFcq2dPTo95bWGUSj5KdPVqYj9CZux': {
    'default': 'UpdateValidatorListBalance',
  },
  // X1 Memo Minter
  '8iq6zqaEVcfaym2u8t939PAN5jmfPVc6Z333RuxKTTZX': {
    'default': 'ProcessMint',
  },
}

// Parse instruction name from data or logs
const getInstructionName = (
  programId: string,
  instruction: any,
  logMessages?: string[]
): string => {
  // First try parsed type from RPC
  if (instruction.parsed?.type) {
    return instruction.parsed.type
  }
  if (instruction.type && instruction.type !== 'Unknown') {
    return instruction.type
  }

  // Try to parse from instruction data. `instruction.data` is normally a
  // base58 string, but some RPC/enriched shapes deliver it as a number, an
  // object, or the `[data, encoding]` tuple — all of which lack the string
  // methods (.slice/.replace) used below and crashed the whole page
  // ("s.slice is not a function"). Normalize to a string here: accept a raw
  // string or the first element of a `[string, encoding]` tuple; anything else
  // becomes '' and falls through to log-message parsing.
  const rawData = instruction.data

  // Token-2022 ConfidentialTransfer recognizer (data[0]===0x1b). Recognizing it
  // here makes the instruction badge read e.g. "Confidential Transfer" /
  // "Confidential Deposit" instead of "Unknown". See utils/confidentialTransfer.
  const ctInfo = recognizeConfidentialInstruction(programId, rawData)
  if (ctInfo) return ctInfo.name
  if (isTokenReallocate(programId, rawData)) return 'Reallocate (Extension)'

  const data = typeof rawData === 'string'
    ? rawData
    : (Array.isArray(rawData) && typeof rawData[0] === 'string' ? rawData[0] : '')
  if (data && INSTRUCTION_DISCRIMINATORS[programId]) {
    const discriminators = INSTRUCTION_DISCRIMINATORS[programId]

    // For Compute Budget, check first byte
    if (programId === 'ComputeBudget111111111111111111111111111111') {
      try {
        // Data is base58 encoded, decode first byte
        const decoded = atob(data.replace(/[^A-Za-z0-9+/=]/g, ''))
        if (decoded.length > 0) {
          const firstByte = decoded.charCodeAt(0).toString(16).padStart(2, '0')
          if (discriminators[firstByte]) {
            return discriminators[firstByte]
          }
        }
      } catch {
        // Try hex interpretation
        const hexPrefix = data.slice(0, 2).toLowerCase()
        if (discriminators[hexPrefix]) {
          return discriminators[hexPrefix]
        }
      }
    }

    // For Anchor programs, check first 8 bytes (shown as first 6 hex chars)
    const hexPrefix = data.slice(0, 6).toLowerCase()
    if (discriminators[hexPrefix]) {
      return discriminators[hexPrefix]
    }

    // Use default if available
    if (discriminators['default']) {
      return discriminators['default']
    }
  }

  // Try to extract from log messages
  if (logMessages && logMessages.length > 0) {
    for (const log of logMessages) {
      // Look for "Instruction: <name>" pattern
      const match = log.match(/Instruction:\s*(\w+)/i)
      if (match) {
        // Check if this log is for our program
        const programInvoke = `Program ${programId} invoke`
        const logIndex = logMessages.indexOf(log)
        // Find if there's a program invoke before this log
        for (let i = logIndex - 1; i >= 0; i--) {
          if (logMessages[i].includes(programInvoke)) {
            return match[1]
          }
          // If we hit another program invoke, stop
          if (logMessages[i].includes('invoke [1]')) {
            break
          }
        }
      }
    }
  }

  return 'Unknown'
}

/**
 * SwapAssetChip — one side of the swap summary line: token logo + compact
 * amount + symbol (+ name when it adds information). Falls back to a letter
 * badge + short mint until metadata resolves. SPL mints link to /token/:mint.
 */
const SwapAssetChip: React.FC<{
  asset: TxSummaryAsset
  meta?: { symbol?: string; name?: string; logoUrl?: string }
  /** Optional +/− prefix (multi-leg trades and arbitrage show signed legs). */
  sign?: '+' | '−'
}> = ({ asset, meta, sign }) => {
  const [imgFailed, setImgFailed] = React.useState(false)
  const x1 = getX1TokenIdentity(asset.mint)
  const symbol = x1?.symbol || meta?.symbol || asset.symbol
  const name = x1?.name || meta?.name
  const logo = x1?.logo || meta?.logoUrl
  const letter = (symbol || '?').trim().charAt(0).toUpperCase()

  const chip = (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-gray-700/60 pl-1 pr-2.5 py-0.5 max-w-full">
      <span className="h-5 w-5 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 bg-gray-200 dark:bg-gray-600 text-[10px] font-semibold text-gray-600 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-gray-600">
        {logo && !imgFailed ? (
          <img
            src={logo}
            alt={symbol}
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <span aria-hidden="true">{letter}</span>
        )}
      </span>
      <span className="font-semibold text-gray-900 dark:text-white whitespace-nowrap">
        {sign && (
          <span className={sign === '+' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
            {sign}
          </span>
        )}
        {formatCompactAmount(asset.amount)} {symbol}
      </span>
      {name && name !== symbol && (
        <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400 truncate max-w-[10rem]">
          {name}
        </span>
      )}
    </span>
  )

  if (asset.mint && asset.mint !== 'native') {
    return (
      <Link
        to={`/token/${asset.mint}`}
        title={`${name || symbol} — ${asset.mint}`}
        className="hover:opacity-80 transition-opacity min-w-0"
      >
        {chip}
      </Link>
    )
  }
  return <span title={name || symbol} className="min-w-0">{chip}</span>
}

/**
 * ValidatorChip — validator identity for the stake summary line: icon (letter
 * badge fallback) + display name as shown on /validators, linking to the
 * validator's detail page. Falls back to the short vote account until the
 * directory resolves (or if the validator has no registered name).
 */
const ValidatorChip: React.FC<{
  voteAccount: string
  info?: { name?: string; iconUrl?: string }
}> = ({ voteAccount, info }) => {
  const [imgFailed, setImgFailed] = React.useState(false)
  const label = info?.name || `${voteAccount.slice(0, 4)}…${voteAccount.slice(-4)}`
  const letter = (info?.name || voteAccount).trim().charAt(0).toUpperCase()
  return (
    <Link
      to={`/validators/${voteAccount}`}
      title={`${info?.name ? `${info.name} — ` : ''}${voteAccount}`}
      className="hover:opacity-80 transition-opacity min-w-0"
    >
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-gray-700/60 pl-1 pr-2.5 py-0.5 max-w-full">
        <span className="h-5 w-5 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 bg-gray-200 dark:bg-gray-600 text-[10px] font-semibold text-gray-600 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-gray-600">
          {info?.iconUrl && !imgFailed ? (
            <img
              src={info.iconUrl}
              alt={label}
              loading="lazy"
              onError={() => setImgFailed(true)}
              className="h-full w-full object-cover"
            />
          ) : (
            <span aria-hidden="true">{letter}</span>
          )}
        </span>
        <span className={`font-semibold text-gray-900 dark:text-white whitespace-nowrap ${info?.name ? '' : 'font-mono'}`}>
          {label}
        </span>
      </span>
    </Link>
  )
}

export const TransactionDetails: React.FC = () => {
  const { txHash } = useParams<{ txHash: string }>()
  useAskX1PageContext(txHash ? { type: 'transaction', id: txHash } : null)
  const currentNetwork = configService.getCurrentNetworkConfig()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // State for expandable sections
  const [expandedSections, setExpandedSections] = React.useState({
    instructions: false,
    accountKeys: false,
    logMessages: false,
    tokenBalances: false,
    innerInstructions: false
  })

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  // Track if prefetching has been done for this transaction
  const hasPrefetchedRef = React.useRef<string | null>(null)

  // Handle back navigation
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/transactions')
    }
  }

  // Fetch transaction details from backend API (uses historical RPC pool)
  const { data: transaction, isLoading, error } = useQuery({
    queryKey: ['transaction-details-rpc', currentNetwork.name, txHash],
    queryFn: async () => {
      if (!txHash) return null
      log.debug('🔍 Fetching comprehensive transaction details via historical RPC pool...')

      // Fetch from backend API which uses the historical RPC pool (archive nodes)
      const response = await fetch(`/api/rpc/transaction/${txHash}?network=${currentNetwork.name}`)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Transaction not found')
      }

      const txData = await response.json()
      if (!txData) {
        throw new Error('Transaction not found')
      }
      const meta = txData.meta
      const transaction = txData.transaction

      // Determine transaction version
      const version = txData.version !== undefined ? (txData.version === 0 ? 'v0' : `v${txData.version}`) : 'legacy'

      // WP-4380: normalize account-key resolution across v0 and legacy responses.
      // - Legacy responses populate `transaction.message.accountKeys` (string or
      //   {pubkey:string}).
      // - v0 responses populate `transaction.message.staticAccountKeys` (plain
      //   strings) PLUS `meta.loadedAddresses.{writable,readonly}` resolved from
      //   address lookup tables. The page was reading only `accountKeys` so on
      //   every v0 tx (the chain default since 2026) it saw `[]`, From/To both
      //   became 'unknown', and the Account Keys section showed 0.
      const SYSTEM_PROGRAM = '11111111111111111111111111111111'
      const COMPUTE_BUDGET = 'ComputeBudget111111111111111111111111111111'
      const rawStatic = (transaction.message?.staticAccountKeys as any) || (transaction.message?.accountKeys as any) || []
      const loadedWritable = (meta?.loadedAddresses?.writable as any) || []
      const loadedReadonly = (meta?.loadedAddresses?.readonly as any) || []
      const normalizeKey = (k: any) => (typeof k === 'string' ? k : (k?.pubkey || ''))
      const allKeys: string[] = [
        ...rawStatic.map(normalizeKey),
        ...loadedWritable.map(normalizeKey),
        ...loadedReadonly.map(normalizeKey),
      ].filter(Boolean)

      // Fee payer is always the first signer = first static key.
      const fromAddr = allKeys[0] || 'unknown'

      // Primary recipient heuristic mirroring server-side parsed_data writer at
      // rpc-block-stream extractFromTo: first key after [0] that isn't
      // System Program, ComputeBudget, or the signer itself. This is what the
      // backend stores as `parsed_data.to` and what the Recent Transactions
      // widget uses to show "→ to" — the /tx page must agree.
      let toAddr: string = 'unknown'
      for (let i = 1; i < allKeys.length; i++) {
        const k = allKeys[i]
        if (k && k !== SYSTEM_PROGRAM && k !== COMPUTE_BUDGET && k !== fromAddr) {
          toAddr = k
          break
        }
      }
      if (toAddr === 'unknown' && allKeys.length > 1) toAddr = allKeys[1]

      // Extract comprehensive transaction details
      return {
        signature: txHash,
        slot: txData.slot,
        blockTime: txData.blockTime,
        timestamp: new Date((txData.blockTime || Date.now() / 1000) * 1000).toISOString(),
        status: meta?.err ? 'failed' : 'success',
        error: meta?.err,
        fee: meta?.fee || 0,
        version,

        // Compute units
        computeUnitsConsumed: meta?.computeUnitsConsumed || 0,

        // Account keys — WP-4380: unified from v0 + legacy + loaded ALTs
        accountKeys: allKeys,

        // Instructions — WP-4380: v0 puts these in compiledInstructions
        instructions: transaction.message.instructions || transaction.message.compiledInstructions || [],

        // Inner instructions
        innerInstructions: meta?.innerInstructions || [],

        // Log messages
        logMessages: meta?.logMessages || [],

        // Token balances
        preTokenBalances: meta?.preTokenBalances || [],
        postTokenBalances: meta?.postTokenBalances || [],

        // Addresses — WP-4380: from allKeys with primary-recipient heuristic
        fromAddress: fromAddr,
        toAddress: toAddr,

        // Balances
        preBalances: meta?.preBalances || [],
        postBalances: meta?.postBalances || [],

        // Signatures
        signatures: transaction.signatures || [],

        // Recent blockhash
        recentBlockhash: transaction.message.recentBlockhash,

        // Raw data for size calculation
        rawData: txData
      }
    },
    enabled: !!txHash,
  })

  // Prefetch linked resources in the background (2 layers deep)
  // Disabled temporarily - runs silently in background without affecting UI
  React.useEffect(() => {
    // DISABLED: Prefetching was causing page glitches
    // Will re-enable with proper implementation later
    return
  }, [])

  // Batch resolve X1NS names for all account addresses in the transaction
  // Must be before early returns to satisfy Rules of Hooks
  const accountAddresses = React.useMemo(() =>
    transaction?.accountKeys?.map((a: any) => a.pubkey || a).filter(Boolean) || [],
    [transaction?.accountKeys]
  )
  const { names: accountNames } = useAddressNames(accountAddresses)

  // Distinct SPL mints referenced by this tx's token balances. Used to enrich
  // the one-line summary + Token Balances rows with real symbols/logos. Cheap,
  // cached (5min in tokenMetadataService), and fully non-blocking — the UI
  // degrades to a short-mint label while it resolves.
  const tokenMints = React.useMemo(() => {
    const set = new Set<string>()
    ;(transaction?.preTokenBalances || []).forEach((b: any) => b?.mint && set.add(b.mint))
    ;(transaction?.postTokenBalances || []).forEach((b: any) => b?.mint && set.add(b.mint))
    return [...set]
  }, [transaction?.preTokenBalances, transaction?.postTokenBalances])

  const { data: tokenMeta } = useQuery({
    queryKey: ['tx-token-meta', currentNetwork.name, tokenMints],
    enabled: tokenMints.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const out: Record<string, { symbol?: string; name?: string; logoUrl?: string }> = {}
      await Promise.all(
        tokenMints.map(async (mint) => {
          try {
            const m = await tokenMetadataService.getTokenMetadata(mint)
            out[mint] = { symbol: m.symbol, name: m.name, logoUrl: m.image }
          } catch {
            /* best-effort: leave unresolved, UI falls back to short mint */
          }
        }),
      )
      return out
    },
  })

  // mint -> symbol map for the summary detector (empty until metadata resolves).
  const tokenSymbols = React.useMemo(() => {
    const map: Record<string, string> = {}
    if (tokenMeta) {
      for (const [mint, m] of Object.entries(tokenMeta)) {
        if (m?.symbol) map[mint] = m.symbol
      }
    }
    return map
  }, [tokenMeta])

  // One-line, human-readable summary of what this tx did. Computed as a hook
  // (not post-early-return) so dependent queries below can key off it.
  const summary = React.useMemo(
    () => (transaction ? buildTxSummary(transaction, { tokenSymbols }) : null),
    [transaction, tokenSymbols],
  )

  // Stake delegations: resolve the validator's display identity (name + icon,
  // as shown on /validators) from the vote account. Cached 10min; only fetched
  // when this tx actually is a delegation.
  const delegateVoteAccount = summary?.stake?.voteAccount
  const { data: validatorDir } = useQuery({
    queryKey: ['validator-name-dir', currentNetwork.name],
    enabled: !!delegateVoteAccount,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch(`/api/validators/list?network=${currentNetwork.name}`)
      if (!res.ok) return {} as Record<string, { name?: string; iconUrl?: string }>
      const body = await res.json()
      const items: any[] = Array.isArray(body) ? body : body.validators || body.data || []
      const out: Record<string, { name?: string; iconUrl?: string }> = {}
      for (const v of items) {
        if (v?.votePubkey) {
          out[v.votePubkey] = {
            name: v.name || undefined,
            iconUrl: v.iconUrl || v.icon_url || undefined,
          }
        }
      }
      return out
    },
  })

  const formatHash = (hash: string, length: number = 10) => {
    if (!hash) return 'N/A'
    return `${hash.slice(0, length)}...${hash.slice(-8)}`
  }

  // LIVE program name/icon resolution for the instruction list (outer + inner).
  // Computed before the early returns so the hook order is stable; starts empty
  // during load and populates once `transaction` resolves.
  const instructionProgramIds = React.useMemo<string[]>(() => {
    const t: any = transaction
    if (!t) return []
    const set = new Set<string>()
    const push = (id: unknown) => {
      if (typeof id === 'string' && id) set.add(id)
    }
    if (Array.isArray(t.instructions)) {
      for (const ix of t.instructions) push(resolveInstructionProgramId(ix, t.accountKeys))
    }
    if (Array.isArray(t.innerInstructions)) {
      for (const inner of t.innerInstructions) {
        if (Array.isArray(inner.instructions)) {
          for (const ix of inner.instructions) push(resolveInstructionProgramId(ix, t.accountKeys))
        }
      }
    }
    return [...set]
  }, [transaction])
  const { getProgramDisplay } = useProgramLabels(instructionProgramIds)

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="card">
                <div className="card-content space-y-4">
                  {Array.from({ length: 6 }).map((_, j) => (
                    <div key={j} className="flex justify-between">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error || !transaction) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={handleBack} className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Transaction Not Found</h1>
        </div>
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">
            Transaction {txHash} could not be found on the {currentNetwork.displayName} network.
          </p>
        </div>
      </div>
    )
  }

  // Calculate transaction stats
  const feeInXNT = transaction.fee / 1e9
  const instructionCount = transaction.instructions?.length || 0
  const innerInstructionCount = transaction.innerInstructions?.reduce((acc: number, inner: any) => acc + (inner.instructions?.length || 0), 0) || 0
  const totalInstructionCount = instructionCount + innerInstructionCount
  const accountCount = transaction.accountKeys?.length || 0
  const logCount = transaction.logMessages?.length || 0
  const dataSize = JSON.stringify(transaction.rawData).length
  const hasTokenBalances = transaction.preTokenBalances?.length > 0 || transaction.postTokenBalances?.length > 0

  // Token accounts whose 0-Change is actually a HIDDEN amount moved by a private
  // Confidential Transfer (Token-2022 CT). Their public SPL `amount` is untouched
  // by design, so pre/post balances read 0 — we label those rows "Encrypted"
  // instead of a misleading "-". Deposit/Withdraw are excluded (they reveal a
  // real amount and already show a correct nonzero Change). Precise: only the
  // accounts the private transfer actually references are flagged.
  const ctParticipants = confidentialTransferParticipants(
    transaction.instructions,
    transaction.accountKeys
  )
  const isEncryptedChangeRow = (accountIndex: number, mint: string) =>
    ctParticipants.accountIndexes.has(accountIndex) &&
    (ctParticipants.mints.size === 0 || ctParticipants.mints.has(mint))

  // Calculate balance changes
  const balanceChanges = transaction.accountKeys.map((account: any, index: number) => {
    const preBalance = transaction.preBalances[index] || 0
    const postBalance = transaction.postBalances[index] || 0
    const change = postBalance - preBalance
    return {
      address: account.pubkey || account,
      preBalance,
      postBalance,
      change,
      changeInXNT: change / 1e9
    }
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3 md:space-x-4">
        <button onClick={handleBack} className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex-shrink-0">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white">
            Transaction Details
          </h1>
          <p className="mt-1 md:mt-2 text-sm md:text-base text-gray-600 dark:text-gray-400">
            Comprehensive transaction information on {currentNetwork.displayName}
          </p>
        </div>
      </div>

      {/* One-line human-readable summary (cherry-pick #1) */}
      {summary && (
      <div className="card">
        <div className="card-content">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-50 dark:bg-primary-900/30 flex-shrink-0">
              <summary.Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            {summary.stake ? (
              <div className="flex items-center flex-wrap gap-x-2 gap-y-1.5 text-sm md:text-base font-medium text-gray-900 dark:text-white min-w-0">
                <span>Delegated stake to</span>
                <ValidatorChip
                  voteAccount={summary.stake.voteAccount}
                  info={validatorDir?.[summary.stake.voteAccount]}
                />
              </div>
            ) : summary.lp ? (
              <div className="flex items-center flex-wrap gap-x-2 gap-y-1.5 text-sm md:text-base font-medium text-gray-900 dark:text-white min-w-0">
                <span>{summary.lp.label}</span>
                {summary.lp.assets.map((a, i) => (
                  <React.Fragment key={`lp-${a.mint}`}>
                    {i > 0 && <span className="text-gray-400 dark:text-gray-500">+</span>}
                    <SwapAssetChip asset={a} meta={tokenMeta?.[a.mint]} />
                  </React.Fragment>
                ))}
                <span className="text-gray-500 dark:text-gray-400 font-normal">
                  on {summary.lp.venue}
                </span>
              </div>
            ) : summary.transfer ? (
              <div className="flex items-center flex-wrap gap-x-2 gap-y-1.5 text-sm md:text-base font-medium text-gray-900 dark:text-white min-w-0">
                <span>{summary.transfer.direction === 'received' ? 'Received' : 'Sent'}</span>
                {summary.transfer.isNft && <span>an NFT</span>}
                {!summary.transfer.isNft && (
                  <SwapAssetChip
                    asset={summary.transfer.asset}
                    meta={tokenMeta?.[summary.transfer.asset.mint]}
                  />
                )}
                {summary.transfer.counterparty && (
                  <>
                    <span className="text-gray-500 dark:text-gray-400 font-normal">
                      {summary.transfer.direction === 'received' ? 'from' : 'to'}
                    </span>
                    <Link
                      to={`/account/${summary.transfer.counterparty}`}
                      className="font-mono text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {`${summary.transfer.counterparty.slice(0, 4)}…${summary.transfer.counterparty.slice(-4)}`}
                    </Link>
                  </>
                )}
              </div>
            ) : summary.trade ? (
              <div className="flex items-center flex-wrap gap-x-2 gap-y-1.5 text-sm md:text-base font-medium text-gray-900 dark:text-white min-w-0">
                <span>Traded</span>
                {summary.trade.sent.map((a) => (
                  <SwapAssetChip key={`s-${a.mint}`} asset={a} meta={tokenMeta?.[a.mint]} sign="−" />
                ))}
                {summary.trade.sent.length > 0 && summary.trade.received.length > 0 && (
                  <ArrowRight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" aria-label="for" />
                )}
                {summary.trade.received.map((a) => (
                  <SwapAssetChip key={`r-${a.mint}`} asset={a} meta={tokenMeta?.[a.mint]} sign="+" />
                ))}
                <span className="text-gray-500 dark:text-gray-400 font-normal">
                  on {summary.trade.venue}
                </span>
              </div>
            ) : summary.swap ? (
              <div className="flex items-center flex-wrap gap-x-2 gap-y-1.5 text-sm md:text-base font-medium text-gray-900 dark:text-white min-w-0">
                <span>Swapped</span>
                <SwapAssetChip
                  asset={summary.swap.sent}
                  meta={tokenMeta?.[summary.swap.sent.mint]}
                />
                <ArrowRight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" aria-label="for" />
                <SwapAssetChip
                  asset={summary.swap.received}
                  meta={tokenMeta?.[summary.swap.received.mint]}
                />
                <span className="text-gray-500 dark:text-gray-400 font-normal">
                  on {summary.swap.venue}
                </span>
              </div>
            ) : (
              <p className="text-sm md:text-base font-medium text-gray-900 dark:text-white break-words min-w-0">
                {summary.text}
              </p>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Transaction Status Banner */}
      <div className="card">
        <div className="card-content">
          <div className="flex items-center space-x-4">
            {transaction.status === 'success' ? (
              <CheckCircle className="h-8 w-8 text-green-500 flex-shrink-0" />
            ) : (
              <XCircle className="h-8 w-8 text-red-500 flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Transaction {transaction.status === 'success' ? 'Successful' : 'Failed'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 truncate">
                {formatDistanceToNow(new Date(transaction.timestamp), { addSuffix: true })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Three Overview Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Transaction Information Card */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Database className="h-5 w-5 text-gray-500" />
              <h2 className="card-title">Transaction Information</h2>
            </div>
          </div>
          <div className="card-content space-y-4">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Signature</span>
              <div className="flex items-center space-x-2 min-w-0">
                <span className="text-xs font-mono text-gray-900 dark:text-white break-all">
                  {formatHash(transaction.signature, 12)}
                </span>
                <CopyButton text={transaction.signature} />
              </div>
            </div>

            <div className="flex justify-between items-start">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</span>
              <span className={`badge text-xs ${
                transaction.status === 'success'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }`}>
                {transaction.status}
              </span>
            </div>

            <div className="flex justify-between items-start">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Slot</span>
              <Link
                to={`/block/${transaction.slot}`}
                className="text-sm font-mono text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                {transaction.slot.toLocaleString()}
              </Link>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Timestamp</span>
              <div className="text-right">
                <div className="text-sm text-gray-900 dark:text-white">
                  {new Date(transaction.timestamp).toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDistanceToNow(new Date(transaction.timestamp), { addSuffix: true })}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Recent Blockhash</span>
              <div className="flex items-center space-x-2">
                <span className="text-xs font-mono text-gray-900 dark:text-white break-all">
                  {formatHash(transaction.recentBlockhash, 8)}
                </span>
                <CopyButton text={transaction.recentBlockhash} />
              </div>
            </div>

            <div className="flex justify-between items-start">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Confirmation Status</span>
              <span className="badge bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs">
                Finalized
              </span>
            </div>

            <div className="flex justify-between items-start">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Transaction Version</span>
              <span className="badge badge-secondary text-xs">
                {transaction.version}
              </span>
            </div>
          </div>
        </div>

        {/* Compute & Fees Card */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Zap className="h-5 w-5 text-gray-500" />
              <h2 className="card-title">Compute & Fees</h2>
            </div>
          </div>
          <div className="card-content space-y-4">
            <div className="flex justify-between items-start">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Transaction Fee</span>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {feeInXNT.toFixed(9)} XNT
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {transaction.fee.toLocaleString()} lamports
                </div>
              </div>
            </div>

            <div className="flex justify-between items-start">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Compute Units</span>
              <div className="text-right">
                <div className="text-sm text-gray-900 dark:text-white">
                  {transaction.computeUnitsConsumed.toLocaleString()} CU
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  consumed
                </div>
              </div>
            </div>

            <div className="flex justify-between items-start">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Fee per CU</span>
              <span className="text-sm text-gray-900 dark:text-white">
                {transaction.computeUnitsConsumed > 0
                  ? ((transaction.fee / transaction.computeUnitsConsumed) * 1e6).toFixed(6)
                  : '0'} µXNT
              </span>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cost</span>
                <div className="text-right">
                  <div className="text-base font-semibold text-gray-900 dark:text-white">
                    {feeInXNT.toFixed(9)} XNT
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    ≈ ${(feeInXNT * 0.1).toFixed(4)} USD
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Priority Fee</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Standard transaction
              </span>
            </div>
          </div>
        </div>

        {/* Transaction Stats Card */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5 text-gray-500" />
              <h2 className="card-title">Transaction Stats</h2>
            </div>
          </div>
          <div className="card-content space-y-3">
            {/* Instructions Group */}
            <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Total Instructions</span>
                <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
                  {totalInstructionCount}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Top-level</div>
                  <div className="text-base font-semibold text-gray-900 dark:text-white">{instructionCount}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Inner</div>
                  <div className="text-base font-semibold text-gray-900 dark:text-white">{innerInstructionCount}</div>
                </div>
              </div>
            </div>

            {/* Account & Logs Group */}
            <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Account Keys</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{accountCount}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Log Messages</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{logCount}</div>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Transaction Size</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {(dataSize / 1024).toFixed(2)} KB
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Token Transfers</span>
                <span className={`badge text-xs font-medium ${
                  hasTokenBalances
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                }`}>
                  {hasTokenBalances ? 'Yes' : 'None'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Address Flow */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center space-x-2">
            <ArrowRightLeft className="h-5 w-5 text-gray-500" />
            <h2 className="card-title">Address Flow</h2>
          </div>
        </div>
        <div className="card-content">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">From (Fee Payer)</div>
              <AddressWithName
                address={transaction.fromAddress}
                showName={true}
                namePosition="replace"
                truncateLength={12}
                size="sm"
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              />
            </div>

            <div className="flex md:mx-4 justify-center">
              <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primary-100 dark:bg-primary-900 rounded-full flex-shrink-0">
                <ArrowRightLeft className="h-5 w-5 md:h-6 md:w-6 text-primary-600 dark:text-primary-400 rotate-90 md:rotate-0" />
              </div>
            </div>

            <div className="flex-1 min-w-0 md:text-right">
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">To (Primary Recipient)</div>
              <div className="flex md:justify-end">
                <AddressWithName
                  address={transaction.toAddress}
                  showName={true}
                  namePosition="replace"
                  truncateLength={12}
                  size="sm"
                  className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions Section - Expandable */}
      <div className="card">
        <div
          className="card-header cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          onClick={() => toggleSection('instructions')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Code className="h-5 w-5 text-gray-500" />
              <h2 className="card-title">
                Instructions ({totalInstructionCount})
              </h2>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Click to {expandedSections.instructions ? 'collapse' : 'expand'}
              </span>
              {expandedSections.instructions ? (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              )}
            </div>
          </div>
        </div>

        {expandedSections.instructions && (
          <div className="card-content p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Program
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Instruction
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Accounts
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {transaction.instructions.map((instruction: any, index: number) => {
                    const programId = resolveInstructionProgramId(instruction, transaction.accountKeys)
                    const meta = getProgramDisplay(programId)
                    const instructionType = getInstructionName(programId, instruction, transaction.logMessages)
                    const accountsInvolved = instruction.accounts?.length || 0

                    // Extract transfer details if it's a transfer instruction
                    const transferInfo = instruction.parsed?.info
                    const hasTransferAmount = transferInfo?.lamports !== undefined

                    return (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4">
                          {programId ? (
                            <Link
                              to={`/program/${programId}`}
                              title={`${meta.name} — ${programId}`}
                              className="group inline-flex items-center gap-2.5 rounded-pill -mx-1.5 px-1.5 py-1 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
                            >
                              {meta.iconUrl ? (
                                <img
                                  src={meta.iconUrl}
                                  alt=""
                                  className="h-4 w-4 flex-shrink-0 rounded-sm object-contain"
                                  loading="lazy"
                                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                                />
                              ) : (
                                <meta.Icon className={`h-4 w-4 flex-shrink-0 ${meta.color}`} />
                              )}
                              <div className="text-sm min-w-0">
                                <div className="flex items-center gap-1 font-medium text-gray-900 group-hover:text-primary-600 dark:text-white dark:group-hover:text-primary-400">
                                  {meta.name}
                                  {meta.verified && (
                                    <BadgeCheck className="h-3.5 w-3.5 flex-shrink-0 text-sky-500 dark:text-sky-400" aria-label="Verified program" />
                                  )}
                                </div>
                                <div className="text-xs font-mono text-primary-600 group-hover:text-primary-700 dark:text-primary-400 dark:group-hover:text-primary-300">
                                  {formatHash(programId, 8)}
                                </div>
                              </div>
                            </Link>
                          ) : (
                            <div className="flex items-center gap-2.5">
                              {meta.iconUrl ? (
                                <img
                                  src={meta.iconUrl}
                                  alt=""
                                  className="h-4 w-4 flex-shrink-0 rounded-sm object-contain"
                                  loading="lazy"
                                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                                />
                              ) : (
                                <meta.Icon className={`h-4 w-4 flex-shrink-0 ${meta.color}`} />
                              )}
                              <div className="text-sm min-w-0">
                                <div className="font-medium text-gray-900 dark:text-white">{meta.name}</div>
                                <div className="text-xs font-mono text-gray-500 dark:text-gray-400">Unknown program</div>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <span className="badge badge-secondary text-xs">
                              {instructionType}
                            </span>
                            {hasTransferAmount && (
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                {(transferInfo.lamports / 1e9).toFixed(9)} XNT
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {accountsInvolved} accounts
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Confidential Transfer evidence panel (Token-2022 CT) — display-only */}
            <ConfidentialTransferPanel transaction={transaction} accountKeys={accountAddresses} />

            {/* Inner Instructions */}
            {innerInstructionCount > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                  <Layers className="h-4 w-4" />
                  <span>Inner Instructions ({innerInstructionCount})</span>
                </h3>
                <div className="space-y-4">
                  {transaction.innerInstructions.map((inner: any, outerIndex: number) => (
                    <div key={outerIndex} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-3">
                        Inner instructions for instruction #{inner.index + 1}
                      </div>
                      <div className="space-y-2">
                        {inner.instructions.map((instruction: any, innerIndex: number) => {
                          const innerProgramId = resolveInstructionProgramId(instruction, transaction.accountKeys)
                          const innerMeta = getProgramDisplay(innerProgramId)
                          const instructionType = getInstructionName(innerProgramId, instruction, transaction.logMessages)

                          return (
                            <div key={innerIndex} className="flex items-center justify-between text-sm p-2 bg-white dark:bg-gray-800 rounded">
                              <div className="flex items-center space-x-3">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {outerIndex + 1}.{innerIndex + 1}
                                </span>
                                {innerProgramId ? (
                                  <Link
                                    to={`/program/${innerProgramId}`}
                                    title={`${innerMeta.name} — ${innerProgramId}`}
                                    className="group inline-flex items-center gap-2 rounded-md px-1 -mx-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700/60"
                                  >
                                    <innerMeta.Icon className={`h-3.5 w-3.5 flex-shrink-0 ${innerMeta.color}`} />
                                    <span className="inline-flex items-center gap-1 font-medium text-gray-900 group-hover:text-primary-600 dark:text-white dark:group-hover:text-primary-400">
                                      {innerMeta.name}
                                      {innerMeta.verified && (
                                        <BadgeCheck className="h-3 w-3 flex-shrink-0 text-sky-500 dark:text-sky-400" aria-label="Verified program" />
                                      )}
                                    </span>
                                  </Link>
                                ) : (
                                  <>
                                    <innerMeta.Icon className={`h-3.5 w-3.5 flex-shrink-0 ${innerMeta.color}`} />
                                    <span className="font-medium text-gray-900 dark:text-white">
                                      {innerMeta.name}
                                    </span>
                                  </>
                                )}
                              </div>
                              <span className="badge badge-secondary text-xs">
                                {instructionType}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Account Keys Section - Expandable */}
      <div className="card">
        <div
          className="card-header cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          onClick={() => toggleSection('accountKeys')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Eye className="h-5 w-5 text-gray-500" />
              <h2 className="card-title">
                Account Keys ({accountCount})
              </h2>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Click to {expandedSections.accountKeys ? 'collapse' : 'expand'}
              </span>
              {expandedSections.accountKeys ? (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              )}
            </div>
          </div>
        </div>

        {expandedSections.accountKeys && (
          <div className="card-content p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Address
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Change (XNT)
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Post Balance (XNT)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {transaction.accountKeys.map((account: any, index: number) => {
                    const address = account.pubkey || account
                    const isSigner = account.signer || false
                    const isWritable = account.writable || false
                    const balanceChange = balanceChanges[index]

                    return (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <AddressDisplay
                              address={address}
                              name={accountNames[address]}
                              truncateLength={12}
                              truncateEnd={8}
                              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                            />
                            <CopyButton text={address} />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {balanceChange && balanceChange.change !== 0 ? (
                            <div className="text-sm">
                              <div className={`font-medium ${
                                balanceChange.change > 0
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400'
                              }`}>
                                {balanceChange.change > 0 ? '+' : ''}{balanceChange.changeInXNT.toFixed(9)}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-900 dark:text-white">0</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                          {balanceChange ? (balanceChange.postBalance / 1e9).toFixed(9) : '0'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {index === 0 && (
                              <span className="badge badge-primary text-xs">
                                Fee Payer
                              </span>
                            )}
                            {isSigner && (
                              <span className="badge bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 text-xs">
                                Signer
                              </span>
                            )}
                            {isWritable && (
                              <span className="badge bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 text-xs">
                                Writable
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Log Messages Section - Expandable */}
      {logCount > 0 && (
        <div className="card">
          <div
            className="card-header cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            onClick={() => toggleSection('logMessages')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-gray-500" />
                <h2 className="card-title">
                  Log Messages ({logCount})
                </h2>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Click to {expandedSections.logMessages ? 'collapse' : 'expand'}
                </span>
                {expandedSections.logMessages ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                )}
              </div>
            </div>
          </div>

          {expandedSections.logMessages && (
            <div className="card-content">
              <div className="bg-gray-900 dark:bg-black rounded-lg p-4 overflow-x-auto">
                <div className="space-y-1">
                  {transaction.logMessages.map((log: string, index: number) => (
                    <div key={index} className="font-mono text-xs">
                      <span className="text-gray-500 select-none">{String(index + 1).padStart(3, '0')} │ </span>
                      <span className="text-green-400">{log}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Token Balances Section - Expandable */}
      {hasTokenBalances && (
        <div className="card">
          <div
            className="card-header cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            onClick={() => toggleSection('tokenBalances')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Coins className="h-5 w-5 text-gray-500" />
                <h2 className="card-title">
                  Token Balances ({transaction.preTokenBalances.length})
                </h2>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Click to {expandedSections.tokenBalances ? 'collapse' : 'expand'}
                </span>
                {expandedSections.tokenBalances ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                )}
              </div>
            </div>
          </div>

          {expandedSections.tokenBalances && (
            <div className="card-content p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Account
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Mint
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Pre Balance
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Post Balance
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Change
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {transaction.preTokenBalances.map((preBalance: any, index: number) => {
                      const postBalance = transaction.postTokenBalances.find((pb: any) => pb.accountIndex === preBalance.accountIndex)
                      const preAmount = parseFloat(preBalance.uiTokenAmount?.uiAmountString || '0')
                      const postAmount = parseFloat(postBalance?.uiTokenAmount?.uiAmountString || '0')
                      const change = postAmount - preAmount

                      return (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">
                            #{preBalance.accountIndex}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <TokenCell
                                mint={preBalance.mint}
                                symbol={resolveMintSymbol(preBalance.mint, tokenSymbols)}
                                name={tokenMeta?.[preBalance.mint]?.name}
                                logoUrl={tokenMeta?.[preBalance.mint]?.logoUrl}
                                size="sm"
                              />
                              <CopyButton text={preBalance.mint} />
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                            {preAmount.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                            {postAmount.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            {change !== 0 ? (
                              <span className={`text-sm font-medium ${
                                change > 0
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400'
                              }`}>
                                {change > 0 ? '+' : ''}{change.toLocaleString()}
                              </span>
                            ) : isEncryptedChangeRow(preBalance.accountIndex, preBalance.mint) ? (
                              <span
                                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
                                title="A real amount moved via a private Confidential Transfer, but it is ElGamal-encrypted and not publicly readable — so the account's public balance is unchanged."
                              >
                                🔒 Encrypted
                              </span>
                            ) : (
                              <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
